import axios from "axios";
import { toast } from "react-toastify";

export const API_BASE_URL = "https://www.sensibleacademy.com/backend"; // Make sure this matches your exact path

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add token
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // CRITICAL: If sending FormData, we must delete the global Content-Type 
    // so the browser can automatically generate the multipart boundary string.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    console.log("API Request:", config.method.toUpperCase(), config.url); // Debug log
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
API.interceptors.response.use(
  (response) => {
    console.log("API Response:", response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error("API Error:", error.message);
    console.error("Error URL:", error.config?.url);
    console.error("Error Status:", error.response?.status);

    // IMPORTANT: Check if this is a login attempt
    const isLoginEndpoint = error.config?.url?.includes("/auth/login");

    // For login endpoint failures - just pass through, let Login.jsx handle it
    if (isLoginEndpoint) {
      console.log("Login endpoint error - passing to component");
      return Promise.reject(error);
    }

    // For non-login endpoints, handle normally
    if (error.code === "ECONNABORTED") {
      toast.error("Request timeout. Please try again.");
    } else if (error.response) {
      switch (error.response.status) {
        case 401:
          const errorMsg401 = error.response?.data?.message || "";

          // Check if it's a session expiration from another device
          if (
            errorMsg401.includes("logged out from another device") ||
            errorMsg401.includes("Session expired")
          ) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("is_default_password");
            localStorage.removeItem("last_password_change");

            const allKeys = Object.keys(localStorage);
            allKeys.forEach((key) => {
              if (
                key.startsWith("defaultPwd_seen_") ||
                key.startsWith("reminder_skip_")
              ) {
                localStorage.removeItem(key);
              }
            });

            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
              toast.error(
                "You have been logged out from another device. Please login again.",
              );
            }
          } else {
            // For protected endpoints without specific message
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
              toast.error("Session expired. Please login again.");
            }
          }
          break;
        case 403:
          toast.error("You do not have permission to perform this action");
          break;
        case 404:
          toast.error("Resource not found");
          break;
        case 422:
          const errors = error.response.data.errors;
          if (errors) {
            Object.values(errors).forEach((msg) => toast.error(msg));
          } else {
            toast.error(error.response.data?.message || "Validation error");
          }
          break;
        default:
          toast.error(error.response.data?.message || "Something went wrong");
      }
    } else if (error.request) {
      toast.error(
        "Cannot connect to server. Please check if backend is running.",
      );
    } else {
      toast.error("Network error. Please check your connection.");
    }

    return Promise.reject(error);
  },
);

export default API;
